import { Download02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  useCreateGramWithStorage,
  useGetGramByUserAndEvent,
  useGetGramFileUrl,
  useIsEventSpeakerOrModerator,
} from '@sunrise-ui/api-client';
import {
  AuthFlowError,
  Colors,
  TextLight,
  useAuthStore,
  useSnackbar,
} from '@sunrise-ui/primitives';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';

type Props = {
  userId: string;
};

// Entertaining phrases for the generation wait period
const GENERATING_PHRASES = [
  'Aligning the pixels...',
  'Taking inspiration from Michelangelo...',
  'Mixing digital paint...',
  'Consulting the design gods...',
  'Sprinkling some magic dust...',
  'Channeling creative energy...',
  'Perfecting the composition...',
  'Adding a touch of brilliance...',
  'Crafting your masterpiece...',
  'Summoning artistic muses...',
  'Fine-tuning the aesthetics...',
  'Applying the golden ratio...',
];

export function DownloadSocialGramButton({ userId }: Props) {
  const [generatingCooldownEnd, setGeneratingCooldownEnd] = useState<
    number | null
  >(null);
  const [hasGeneratedGram, setHasGeneratedGram] = useState(false);
  const [isLoadingStorageCheck, setIsLoadingStorageCheck] = useState(true);
  const [currentTypedText, setCurrentTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isBackspacing, setIsBackspacing] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const phraseRotationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const shineAnimation = useRef(new Animated.Value(-300)).current;
  const { currentUserId } = useAuthStore();
  const { showSnackbar } = useSnackbar();

  // Only show button on user's own profile
  const isOwnProfile = userId === currentUserId;

  // Get environment-specific event ID
  const getEventId = (): string => {
    // Check for explicit environment variables first
    if (typeof process !== 'undefined' && process.env) {
      const stage = process.env.EXPO_PUBLIC_STAGE || process.env.STAGE;

      if (stage === 'local' || stage === 'dev') {
        return '604f16fc36584b918e6fb7723d8e401e'; // DEV
      } else if (stage === 'staging' || stage === 'prod') {
        return '12030215d3824606bfdb114612bc3ca4'; // PROD
      }
    }

    // Fallback logic based on current domain (for web)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const { hostname } = window.location;
      if (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.includes('srs-dev.com')
      ) {
        return '604f16fc36584b918e6fb7723d8e401e'; // DEV
      } else if (hostname.includes('joincommuniful.com')) {
        return '12030215d3824606bfdb114612bc3ca4'; // PROD
      }
    }

    // Default to DEV for development
    return '604f16fc36584b918e6fb7723d8e401e';
  };

  const eventId = getEventId();

  // Create AsyncStorage key for this user's gram generation status
  const getStorageKey = (): string => {
    return `hasGeneratedSocialGram_${userId}_${eventId}`;
  };

  // Check if user is speaker or moderator for the event
  const { isAllowed: isSpeakerOrModerator } = useIsEventSpeakerOrModerator(
    eventId,
    userId,
    { enabled: isOwnProfile },
  );

  // Determine Google Drive folder based on role
  const getDriveFolderId = (): string => {
    return isSpeakerOrModerator
      ? '15Nlb7kHYFRkUXTyZqQTgWqjGf3ZMIMWM' // FOR SPEAKERS + MODERATORS
      : '1a_EbBqivsMyVkESkOLsqqxlgAedxrtEg'; // FOR ATTENDEES
  };

  // Only fetch gram data if we know the user has previously generated one
  const {
    data: gramByUserEvent,
    isLoading: isLoadingGramByUserEvent,
    refetch: refetchGramByUserEvent,
  } = useGetGramByUserAndEvent(userId, eventId, {
    enabled: isOwnProfile && hasGeneratedGram,
  });

  // Fetch the file URL for the specific gram
  const { data: gramFileUrl } = useGetGramFileUrl(gramByUserEvent?.id, {
    enabled: isOwnProfile && hasGeneratedGram && Boolean(gramByUserEvent?.id),
  });

  // Hook for generating grams
  const { mutateAsync: generateGram } = useCreateGramWithStorage();

  // Check if generation is in cooldown
  const isGenerationInCooldown =
    generatingCooldownEnd && Date.now() < generatingCooldownEnd;

  // Type out text character by character
  const typeText = (text: string, callback?: () => void) => {
    setIsTyping(true);
    setIsBackspacing(false);
    setCurrentTypedText('');

    let charIndex = 0;
    const typeNextChar = () => {
      if (charIndex < text.length) {
        setCurrentTypedText(text.substring(0, charIndex + 1));
        charIndex++;
        // Vary typing speed slightly for more natural feel (50-120ms)
        const typingDelay = Math.random() * 70 + 50;
        typingTimerRef.current = setTimeout(typeNextChar, typingDelay);
      } else {
        setIsTyping(false);
        // Wait a bit before calling callback (to let user read the full text)
        typingTimerRef.current = setTimeout(() => {
          callback?.();
        }, 1500);
      }
    };

    typeNextChar();
  };

  // Backspace text character by character
  const backspaceText = (callback?: () => void) => {
    setIsBackspacing(true);
    setIsTyping(false);

    const backspaceNextChar = () => {
      setCurrentTypedText((prev) => {
        const newText = prev.substring(0, prev.length - 1);
        if (newText.length > 0) {
          // Vary backspace speed (30-80ms - faster than typing)
          const backspaceDelay = Math.random() * 50 + 30;
          typingTimerRef.current = setTimeout(
            backspaceNextChar,
            backspaceDelay,
          );
        } else {
          setIsBackspacing(false);
          // Small pause before starting next phrase
          typingTimerRef.current = setTimeout(() => {
            callback?.();
          }, 300);
        }
        return newText;
      });
    };

    backspaceNextChar();
  };

  // Start phrase rotation with typing animation
  const startPhraseRotation = () => {
    setCurrentTypedText('');

    const animatePhrase = (phraseIndex: number) => {
      const currentPhrase = GENERATING_PHRASES[phraseIndex];

      typeText(currentPhrase, () => {
        // After typing is complete, wait then backspace
        backspaceText(() => {
          // Move to next phrase
          const nextIndex = (phraseIndex + 1) % GENERATING_PHRASES.length;
          animatePhrase(nextIndex);
        });
      });
    };

    animatePhrase(0);
  };

  // Stop phrase rotation and typing
  const stopPhraseRotation = () => {
    if (phraseRotationTimerRef.current) {
      clearTimeout(phraseRotationTimerRef.current);
      phraseRotationTimerRef.current = null;
    }
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    setIsTyping(false);
    setIsBackspacing(false);
    setCurrentTypedText('');
  };

  // Check AsyncStorage on mount to see if user has already generated their gram
  // For attendees, also check backend if not in AsyncStorage
  useEffect(() => {
    const checkStorageStatus = async () => {
      if (!isOwnProfile) {
        setIsLoadingStorageCheck(false);
        return;
      }

      try {
        const storageKey = getStorageKey();
        const hasGenerated = await AsyncStorage.getItem(storageKey);

        if (hasGenerated) {
          setHasGeneratedGram(true);
          setIsLoadingStorageCheck(false);
          return;
        }

        // For attendees: Check backend if not in AsyncStorage
        if (!isSpeakerOrModerator) {
          try {
            const { data: existingGram } = await refetchGramByUserEvent();
            if (existingGram) {
              // Gram exists! Mark it in storage
              await AsyncStorage.setItem(storageKey, 'true');
              setHasGeneratedGram(true);
              setIsLoadingStorageCheck(false);
              return;
            }
          } catch (error) {
            console.error('Error checking backend for existing gram:', error);
          }
        }

        // No gram found in either AsyncStorage or backend
        setHasGeneratedGram(false);
      } catch (error) {
        console.error('Error checking gram generation status:', error);
        setHasGeneratedGram(false);
      } finally {
        setIsLoadingStorageCheck(false);
      }
    };

    void checkStorageStatus();
  }, [isOwnProfile, userId, eventId, isSpeakerOrModerator]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      if (phraseRotationTimerRef.current) {
        clearTimeout(phraseRotationTimerRef.current);
      }
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  // Helper function to open gram URL
  const openGramUrl = async (awsFileUrl: string) => {
    if (Platform.OS === 'web') {
      // For web: Open in new tab
      if (typeof window !== 'undefined') {
        window.open(awsFileUrl, '_blank');
      }
    } else {
      // For mobile: Open in browser
      const supported = await Linking.canOpenURL(awsFileUrl);
      if (supported) {
        await Linking.openURL(awsFileUrl);
      } else {
        throw new Error('Cannot open gram URL');
      }
    }
  };

  // Blinking cursor effect during typing
  useEffect(() => {
    let cursorInterval: NodeJS.Timeout;

    if (isGenerationInCooldown && (isTyping || isBackspacing)) {
      // Blink cursor every 500ms during typing/backspacing
      cursorInterval = setInterval(() => {
        setShowCursor((prev) => !prev);
      }, 500);
    } else {
      // Always show cursor when not typing
      setShowCursor(true);
    }

    return () => {
      if (cursorInterval) {
        clearInterval(cursorInterval);
      }
    };
  }, [isGenerationInCooldown, isTyping, isBackspacing]);

  // Shine animation effect
  useEffect(() => {
    const createShineAnimation = () => {
      return Animated.sequence([
        Animated.timing(shineAnimation, {
          toValue: 300, // Increased to cover full button width
          duration: 1200,
          useNativeDriver: true,
        }),
        Animated.timing(shineAnimation, {
          toValue: -300, // Increased to match
          duration: 0,
          useNativeDriver: true,
        }),
      ]);
    };

    const runShineLoop = () => {
      createShineAnimation().start(() => {
        // Wait 3 seconds before next shine
        setTimeout(runShineLoop, 3000);
      });
    };

    // Start the first shine after a short delay
    const initialTimeout = setTimeout(runShineLoop, 1000);

    return () => {
      clearTimeout(initialTimeout);
      shineAnimation.stopAnimation();
    };
  }, [shineAnimation]);

  const handleDownloadGram = async () => {
    if (!currentUserId) {
      showSnackbar({
        text: 'Please log in to download gram',
        type: 'error',
      });
      return;
    }

    try {
      // Check if generation is in cooldown
      if (isGenerationInCooldown) {
        const remainingSeconds = Math.ceil(
          (generatingCooldownEnd! - Date.now()) / 1000,
        );
        showSnackbar({
          text: `Gram still generating... Please wait ${remainingSeconds} more seconds`,
          type: 'info',
        });
        return;
      }

      // If user has already generated a gram before, open it directly
      if (hasGeneratedGram) {
        // If data is already loaded, open immediately
        if (gramByUserEvent && gramFileUrl?.aws_file_url) {
          const { aws_file_url } = gramFileUrl;
          await openGramUrl(aws_file_url);

          showSnackbar({
            text: 'Opening your social gram!',
            type: 'success',
          });
          return;
        }

        // Data not loaded yet - fetch it now
        try {
          const { data: fetchedGram } = await refetchGramByUserEvent();

          if (fetchedGram) {
            // Fetch the file URL
            const gramFileUrlResponse = await fetch(
              `${process.env.EXPO_PUBLIC_GRAMS_BASE_URL}/grams/file-url/${fetchedGram.id}`,
            );
            const gramFileUrlData = await gramFileUrlResponse.json();
            const { aws_file_url } = gramFileUrlData;

            if (aws_file_url) {
              await openGramUrl(aws_file_url);
              showSnackbar({
                text: 'Opening your social gram!',
                type: 'success',
              });
              return;
            }
          }
        } catch (error) {
          console.error('Error fetching gram data:', error);
          showSnackbar({
            text: 'Failed to load your gram. Please try again.',
            type: 'error',
          });
          return;
        }
      }

      // Generate new gram
      // For speakers/moderators: Always generate fresh
      // For attendees: Only reached if gram doesn't exist (checked on mount)
      // Set cooldown period (20 seconds from now)
      const cooldownEnd = Date.now() + 20000; // 20 seconds
      setGeneratingCooldownEnd(cooldownEnd);

      // Start phrase rotation
      startPhraseRotation();

      // Show initial notification
      showSnackbar({
        text: 'Generating your social gram... This will take about 20 seconds',
        type: 'info',
      });

      // Call the generate gram endpoint immediately
      await generateGram({
        user_id: userId,
        event_id: eventId,
        google_drive_folder_id: getDriveFolderId(),
        file_format: 'PNG',
      });

      // Mark in AsyncStorage that user has generated their gram
      const storageKey = getStorageKey();
      await AsyncStorage.setItem(storageKey, 'true');
      setHasGeneratedGram(true);

      // Set timer to clear cooldown after 20 seconds
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      cooldownTimerRef.current = setTimeout(async () => {
        setGeneratingCooldownEnd(null);
        stopPhraseRotation();

        // Proactively fetch the gram data so it's ready when user clicks
        try {
          await refetchGramByUserEvent();
          showSnackbar({
            text: 'Gram ready to download!',
            type: 'success',
          });
        } catch (error) {
          console.error('Error fetching gram after generation:', error);
          showSnackbar({
            text: 'Gram generated! Click the button to download',
            type: 'success',
          });
        }
      }, 20000);

      showSnackbar({
        text: 'Gram generation started successfully',
        type: 'success',
      });
    } catch (error) {
      // Clear cooldown on error
      setGeneratingCooldownEnd(null);
      stopPhraseRotation();
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }

      if (error instanceof AuthFlowError) return;

      console.error('Error handling gram download:', error);
      showSnackbar({
        text: 'Failed to generate gram',
        type: 'error',
      });
    }
  };

  // Only show button on user's own profile
  if (!isOwnProfile) {
    return null;
  }

  const getButtonText = () => {
    if (isLoadingStorageCheck) return 'Loading...';
    if (isLoadingGramByUserEvent) return 'Loading...';
    if (isGenerationInCooldown) {
      // Show typed text with blinking cursor
      const cursor = showCursor ? '|' : ' ';
      return currentTypedText + cursor;
    }
    if (hasGeneratedGram) return 'Download Social Gram';
    return 'Generate Social Gram';
  };

  return (
    <View style={styles.buttonContainer}>
      <LinearGradient
        colors={[Colors.purple1, Colors.pink0, Colors.orange0]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradientButton}
      >
        <Pressable
          onPress={handleDownloadGram}
          disabled={isLoadingStorageCheck || isLoadingGramByUserEvent}
          style={[
            styles.buttonPressable,
            (isLoadingStorageCheck || isLoadingGramByUserEvent) &&
              styles.buttonDisabled,
          ]}
        >
          <View style={styles.iconContainer}>
            <HugeiconsIcon
              icon={Download02Icon}
              size={16}
              color={Colors.tint}
            />
          </View>
          <TextLight style={styles.buttonText}>{getButtonText()}</TextLight>
        </Pressable>
        {/* Animated shine overlay */}
        <Animated.View
          style={[
            styles.shineOverlay,
            {
              transform: [{ translateX: shineAnimation }],
            },
          ]}
        >
          <LinearGradient
            colors={['transparent', 'rgba(255, 255, 255, 0.6)', 'transparent']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.shineGradient}
          />
        </Animated.View>
      </LinearGradient>
    </View>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    borderRadius: 30,
  },
  gradientButton: {
    borderRadius: 30,
    padding: 2,
    overflow: 'hidden',
  },
  buttonPressable: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    flexDirection: 'row',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  iconContainer: {
    marginRight: 12,
  },
  buttonText: {
    fontSize: 14,
    color: Colors.text,
  },
  shineOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 30,
    overflow: 'hidden',
  },
  shineGradient: {
    width: 120, // Increased width for wider button
    height: '100%',
    transform: [{ skewX: '-15deg' }],
  },
});
